/**
 * Everything the app needs before it can render, fetched at runtime.
 *
 * The Firebase keys come from `GET /api/config` rather than from `VITE_*`
 * build-time variables on purpose: the same bundle then runs against any
 * project, and the Cloud Run service is the single place the project's identity
 * is configured. The cost is one round trip before first paint, which is hidden
 * behind the splash below.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
  type Auth,
} from 'firebase/auth'
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore'

export interface ServerConfig {
  firebase: {
    apiKey: string
    authDomain: string
    projectId: string
    appId: string
    messagingSenderId: string
    storageBucket: string
  }
  maxImages: number
  maxUploadBytes: number
  researchDepths: string[]
  defaultResearchDepth: string
  /** How much the shopping context box will take, matching the server's cap. */
  maxContextChars: number
  /**
   * `host:port` for each emulator, or null in production. The server sets
   * this — it is the only participant that knows how it was started.
   */
  emulators: { auth: string; firestore: string } | null
}

/** `localhost:8080` → `['localhost', 8080]`. */
function hostPort(value: string): [string, number] {
  const [host, port] = value.split(':')
  return [host, Number(port)]
}

export interface Boot {
  config: ServerConfig
  app: FirebaseApp
  auth: Auth
  db: Firestore
}

let booted: Promise<Boot> | null = null

export function boot(): Promise<Boot> {
  booted ??= (async () => {
    const response = await fetch('/api/config')
    if (!response.ok) throw new Error(`The server would not tell us how to start (${response.status}).`)
    const config = (await response.json()) as ServerConfig

    const app = initializeApp(config.firebase)
    const auth = getAuth(app)
    const db = getFirestore(app)

    if (config.emulators?.auth) {
      // `disableWarnings` only silences the banner the SDK injects into the
      // page, which otherwise sits on top of the app and swallows clicks in
      // the journey tests.
      connectAuthEmulator(auth, `http://${config.emulators.auth}`, {
        disableWarnings: true,
      })
    }
    if (config.emulators?.firestore) {
      connectFirestoreEmulator(db, ...hostPort(config.emulators.firestore))
    }

    // A scan is long and phones lock. Local persistence means coming back to
    // the tab does not mean signing in again.
    await setPersistence(auth, browserLocalPersistence)
    return { config, app, auth, db }
  })()
  return booted
}

export const googleProvider = new GoogleAuthProvider()
